import {
  Component,
  OnDestroy,
  OnInit,
  signal,
  viewChild,
  WritableSignal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { interval } from 'rxjs';
import {
  AORControllerNoteSortOrQuery,
  ControllerNoteCategory,
} from 'src/app/model/controllerNotes/controllerNote';
import {
  CANADA_EST_AORS,
  CANADA_MST_AORS,
  emptyNote,
} from 'src/app/utils/utils';
import { ActionsDropdownComponent } from './actions-dropdown/actions-dropdown.component';
import { MultiSelectChangeEvent } from '@openng/optimus-ui/multiselect';
import { AOR } from 'src/app/model/locations/aor';
import { ControllerNotesContainerComponent } from '../controller-notes-container/controller-notes-container.component';
import { CONTROLLER_NOTES_SEARCH_TABLE_DEFINITION } from '../../definitions/controller-notes-search-table-definition';
import { CLAIMS } from 'src/app/constants/auth-constants';
import {
  LazyLoadFilters,
  PageRequest,
} from 'src/app/core/containers/search-container/search-container.component';
import { GridLazyLoadEvent } from 'src/app/core/components/base-grid.component';
import { NoteFormInput } from 'src/app/model/controllerNotes/sharedData';
import { DatePicker } from '@openng/optimus-ui/datepicker';
import { FormsModule } from '@angular/forms';
import { AorSelectPanelComponent } from './aor-select-panel/aor-select-panel.component';
import { ToggleSwitch } from '@openng/optimus-ui/toggleswitch';
import { ButtonDirective } from '@openng/optimus-ui/button';
import { GeneralGridComponent } from '../../../core/components/general-grid.component';
import { Toast } from '@openng/optimus-ui/toast';
import { ConfirmDialog } from '@openng/optimus-ui/confirmdialog';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-aor-view',
  templateUrl: './aor-view.component.html',
  styleUrls: ['./aor-view.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    DatePicker,
    FormsModule,
    AorSelectPanelComponent,
    ActionsDropdownComponent,
    ToggleSwitch,
    ButtonDirective,
    GeneralGridComponent,
    Toast,
    ConfirmDialog,
    TranslatePipe,
  ],
})
export class AorViewComponent
  extends ControllerNotesContainerComponent
  implements OnInit, OnDestroy
{
  selectedAors: AOR[] = [];
  canCreate = false;
  isESTOnly = false;
  isMSTOnly = false;

  readonly actionsDropdownChild =
    viewChild.required<ActionsDropdownComponent>('actionsDropdown');
  readonly selectedAor: WritableSignal<AOR | null> = signal(null);

  categories = [
    'Compressor Station',
    'M&R',
    'Pipeline',
    'External Caller',
    'System',
    'Valve',
  ];
  availableActions: any[] = [];

  constructor() {
    super();
  }

  override async ngOnInit() {
    const sharedData = this.data();
    this.selectedBusinessUnits.set(sharedData?.businessUnits!);
    this.selectedAor.set(sharedData?.selectedAors[0]!);
    this.startDate = sharedData.startDate;
    this.endDate = sharedData.endDate;

    this.selectedAors.push(this.selectedAor()!);
    this.categories.forEach((type) =>
      this.availableActions.push({ label: `Create ${type} Note` })
    );

    this._tableService.setLastLazyLoad(null);
    this.setTableService(this._tableService);
    this.columns = CONTROLLER_NOTES_SEARCH_TABLE_DEFINITION;

    const refreshInterval = interval(60000);
    this.refreshSubscription = refreshInterval.subscribe(() => {
      this.refreshIntervalLoading = true;
      this.refresh();
    });

    this.canEdit = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.EDIT_CONTROLLER_NOTES,
    ]);
    this.canEditOwn = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.EDIT_OWN_CONTROLLER_NOTES,
    ]);
    this.canDelete = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.DELETE_CONTROLLER_NOTES,
    ]);
    this.canDeleteOwn = this._authApi.doesUserHaveAllClaimsFromList([
      CLAIMS.CONTROL_ROOM.CONTROLLER_NOTES.DELETE_OWN_CONTROLLER_NOTES,
    ]);
    this.canViewMusterList = this._authApi.doesUserHaveClaim(
      CLAIMS.FIELD_OPERATIONS.MUSTER_LIST.VIEW_MUSTER_LIST
    );

    const aor = this.selectedAor()!.name;
    if (CANADA_EST_AORS.includes(aor)) {
      this.isESTOnly = true;
    }
    if (CANADA_MST_AORS.includes(aor)) {
      this.isMSTOnly = true;
    }
    this.applyPreviousData();
    this.applyPreviousFilters();
  }

  override ngOnDestroy(): void {
    this.refreshSubscription?.unsubscribe();
    this.selectedAor.set(null);
    this.data.update((data) => ({
      ...this.data(),
      selectedAors: [],
    }));
  }

  onActionSelected(action: string): void {
    // Only actions available on the aor view page are to create x note, so we're going to cut this down to the note type
    let arr: string[] = action.split(' '); // ['Create', 'External', 'Caller', 'Note']
    let middleSections: string[] = arr.slice(1, -1); // ['External', 'Caller']
    let category = middleSections.join(' ') as ControllerNoteCategory; // 'External Caller'
    // now that we have the note type, we can open the appropriate type of note form
    this.category = category;

    const noteFormInput: NoteFormInput = {
      ...this.data(),
      controllerNote: emptyNote,
      category: this.category,
      isAorView: true,
      formType: 'Create',
    };

    // Delay this by longer to make sure that the modal is showing before the field is cleared, so the user doesn't see it flash
    setTimeout(() => {
      this.openControllerNoteDialog(noteFormInput);
      this.actionsDropdownChild().clear();
    }, 100);
  }

  aorChanged($event: MultiSelectChangeEvent) {
    const desiredAors: AOR[] = $event.value;
    this.selectedAors = desiredAors;
    this.data.update((data) => ({
      ...this.data(),
      selectedAors: this.selectedAors,
    }));
    this.refresh();
    const aorString = desiredAors
      .map((aor) => aor.abbreviation ?? aor.name)
      .join(', ');
    const truncatedAors =
      aorString.length > 30 ? aorString.substring(0, 30) + '...' : aorString;
    const tabLabel = `${truncatedAors} - ${this.startDate?.toLocaleDateString()}`;
    this._tabService.updateActiveTabLabel(tabLabel);
  }

  override handleLazyLoad(
    req: PageRequest,
    $event: GridLazyLoadEvent,
    filters: LazyLoadFilters
  ) {
    this._tableService.setLastLazyLoad($event.lazy);
    const query: AORControllerNoteSortOrQuery = {
      ...this._controllerNotesBuilder.buildControllerNotesQuery(
        filters,
        this.startDate,
        this.endDate,
        this.hideMaintenance
      ),
      aorNames: null,
    };

    // Additional Filtering required to only return notes based on selected AORs
    const extraFilters =
      this.selectedAors.length > 0
        ? { aorNames: this.selectedAors.map((aor) => aor.name) }
        : {};

    if (extraFilters) {
      query.aorNames = extraFilters.aorNames;
    }

    this.queryNetwork(req, $event, query, true);
  }
}
